import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type {
  ActionNodeData,
  ActionNodeAction,
} from '../../../../nodes/action-node/action.node'
import type { BuilderNodeType } from '../../../../types'

type ActionNodePropertyPanelProps = Readonly<{
  id: string
  type: BuilderNodeType
  data: ActionNodeData
  updateData: (data: Partial<ActionNodeData>) => void
}>

const ACTION_OPTIONS: { value: ActionNodeAction['type']; label: string }[] = [
  { value: 'add-tags', label: 'Adicionar etiquetas' },
  { value: 'remove-tags', label: 'Remover etiquetas' },
  { value: 'subscribe-sequence', label: 'Inscrever em sequência' },
  { value: 'unsubscribe-sequence', label: 'Remover de sequência' },
  { value: 'notify-team', label: 'Notificar equipe (WhatsApp/Email)' },
  { value: 'open-conversation', label: 'Abrir conversa para atendente' },
  { value: 'close-conversation', label: 'Fechar conversa do atendente' },
  { value: 'set-custom-fields', label: 'Definir campos personalizados' },
  { value: 'clear-custom-fields', label: 'Limpar campos personalizados' },
]

export default function ActionNodePropertyPanel({
  id,
  data,
  updateData,
}: ActionNodePropertyPanelProps) {
  const actionItems = useMemo(() => data.actions, [data.actions])

  function addAction() {
    const first = ACTION_OPTIONS[0]?.value
    if (!first) return
    updateData({ actions: [...actionItems, { type: first, payload: {} }] })
  }

  function updateAction(index: number, partial: Partial<ActionNodeAction>) {
    const next = actionItems.map((a, i) =>
      i === index ? { ...a, ...partial } : a
    )
    updateData({ actions: next })
  }

  function removeAction(index: number) {
    const next = actionItems.filter((_, i) => i !== index)
    updateData({ actions: next })
  }

  return (
    <div className='flex flex-col gap-4 p-4'>
      <div className='self-end -mb-4 text-xs text-card-foreground/20'>{id}</div>

      <div className='flex flex-col gap-2'>
        <div className='text-xs font-semibold text-card-foreground/60'>
          Ações
        </div>

        {actionItems.length === 0 && (
          <div className='text-xs italic text-card-foreground/60'>
            Nenhuma ação adicionada
          </div>
        )}

        {actionItems.map((action, index) => (
          <div key={index} className='p-3 border rounded-md'>
            <div className='grid grid-cols-1 gap-2 sm:grid-cols-3'>
              <div className='col-span-1'>
                <Label className='block mb-1 text-xs'>Tipo</Label>
                <Select
                  value={action.type}
                  onValueChange={(v: ActionNodeAction['type']) =>
                    updateAction(index, { type: v })
                  }
                >
                  <SelectTrigger className='h-8'>
                    <SelectValue placeholder='Selecione uma ação' />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTION_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className='col-span-2'>
                <Label className='block mb-1 text-xs'>Parâmetros</Label>
                <Input
                  value={(action.payload?.description as string) || ''}
                  onChange={(e) =>
                    updateAction(index, {
                      payload: {
                        ...action.payload,
                        description: e.target.value,
                      },
                    })
                  }
                  placeholder='Ex.: tags: vip,cliente_ouro | sequência: onboarding | equipe: suporte'
                />
              </div>
            </div>
            <div className='flex justify-end mt-3'>
              <Button
                size={'xs'}
                variant={'outline'}
                onClick={() => removeAction(index)}
              >
                Remover ação
              </Button>
            </div>
          </div>
        ))}

        <Button size={'sm'} variant={'outline'} onClick={addAction}>
          Adicionar ação
        </Button>
      </div>

      <div className='flex items-center gap-2 mt-2'>
        <Checkbox
          id='pauseOnOpenForHuman'
          checked={data.pauseOnOpenForHuman}
          onCheckedChange={(checked) =>
            updateData({ pauseOnOpenForHuman: Boolean(checked) })
          }
        />
        <Label htmlFor='pauseOnOpenForHuman' className='text-xs'>
          Pausar automação quando abrir para atendimento humano
        </Label>
      </div>
    </div>
  )
}
